/**
 * Meta TEKİLLEŞTİRİLMİŞ erişim — custom-report sayfaları için ortak katman.
 *
 * Sorun: DB'deki Meta satırları kampanya × gün (veya reklam × gün). `reach`
 * toplanabilir bir metrik değil; satırları toplamak aynı kişiyi her gün ve her
 * kampanyada yeniden sayıyor (Yataş Bedding Eylül: 57,1M ↔ gerçek 13,8M + 2,1M).
 *
 * Çözüm: her Meta metrik nesnesi hangi kampanya/reklamları kapsadığını `_mr`
 * alanında taşır ({ c: Set<campaignId> } ya da { a: Set<adId> }). Erişim değeri
 * ekrana basılırken bu küme için backend `/api/meta-ads/reach-dedup`'a sorulur;
 * Meta kümedeki kişileri tek sayar. Toplam satırları alt satırların kümelerini
 * BİRLEŞTİRİP tekrar sorar — erişimi asla toplamaz.
 *
 * Kullanım:
 *   const reachOf = useMetaReach(API, brandId, startDate, endDate);
 *   reachOf(spec) → number | undefined (yükleniyor) | null (hata)
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const toYmd = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Boş spec */
export const mrNew = () => ({ c: new Set(), a: new Set() });

/** Bir DB satırının kimliğini spec'e ekle (reklam satırıysa ad_id, değilse campaign_id). */
export function mrAddRow(spec, row, { level } = {}) {
  if (!spec || !row) return spec;
  const adId = row.ad_id || row.adId;
  const campId = row.campaign_id || row.campaignId;
  if ((level === 'ad' || (!level && adId)) && adId) spec.a.add(String(adId));
  else if (campId) spec.c.add(String(campId));
  return spec;
}

/** Birden çok spec'i birleştir. */
export function mrUnion(specs) {
  const out = mrNew();
  (specs || []).forEach(s => {
    if (!s) return;
    (s.c || []).forEach(id => out.c.add(id));
    (s.a || []).forEach(id => out.a.add(id));
  });
  return out;
}

const mrIsEmpty = (s) => !s || ((!s.c || s.c.size === 0) && (!s.a || s.a.size === 0));

/**
 * Spec → backend grup anahtarı/gövdesi. Hem kampanya hem reklam içeren karışık
 * spec Meta'da tek filtrede birleşemez; iki parça ayrı sorulur ve toplanır
 * (ender durum — lansman tablosu reklam kümesine çevirerek bundan kaçınır).
 */
function specParts(spec) {
  const parts = [];
  if (spec.c && spec.c.size) parts.push({ key: 'c:' + [...spec.c].sort().join(','), body: { campaigns: [...spec.c] } });
  if (spec.a && spec.a.size) parts.push({ key: 'a:' + [...spec.a].sort().join(','), body: { ads: [...spec.a] } });
  return parts;
}

export function useMetaReach(api, brandId, startDate, endDate) {
  const range = `${brandId}|${toYmd(startDate)}|${toYmd(endDate)}`;
  const [version, setVersion] = useState(0);
  const st = useRef({ range: '', cache: new Map(), queue: new Map(), timer: null });

  if (st.current.range !== range) {
    clearTimeout(st.current.timer);
    st.current = { range, cache: new Map(), queue: new Map(), timer: null };
  }

  const flush = useCallback(async () => {
    const s = st.current;
    s.timer = null;
    if (!s.queue.size || !brandId) return;
    const batch = new Map(s.queue);
    s.queue.clear();
    batch.forEach((_, k) => s.cache.set(k, { state: 'loading' }));
    const groups = {};
    batch.forEach((body, k) => { groups[k] = body; });
    try {
      const res = await fetch(`${api}/api/meta-ads/reach-dedup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-brand-id': brandId },
        body: JSON.stringify({ startDate: toYmd(startDate), endDate: toYmd(endDate), groups }),
      });
      const json = await res.json();
      if (st.current !== s) return; // tarih/marka değişti
      if (!res.ok) throw new Error(json?.error || res.status);
      batch.forEach((_, k) => {
        const v = json.groups?.[k];
        s.cache.set(k, { state: 'ok', value: v == null ? 0 : Number(v), cross: !!json.crossAccount?.[k] });
      });
    } catch (e) {
      console.error('[meta reach-dedup]', e);
      if (st.current !== s) return;
      batch.forEach((_, k) => s.cache.set(k, { state: 'error' }));
    }
    setVersion(v => v + 1);
  }, [api, brandId, startDate, endDate]);

  useEffect(() => () => clearTimeout(st.current.timer), []);

  // version bağımlılığı: sonuç gelince reachOf kimliği değişir → onu kullanan memo'lar yeniden hesaplanır
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reachOf = useCallback((spec) => {
    if (mrIsEmpty(spec)) return undefined;
    const s = st.current;
    let total = 0;
    let pending = false;
    let failed = false;
    specParts(spec).forEach(({ key, body }) => {
      const hit = s.cache.get(key);
      if (!hit) {
        s.queue.set(key, body);
        if (!s.timer) s.timer = setTimeout(flush, 30);
        pending = true;
      } else if (hit.state === 'loading') pending = true;
      else if (hit.state === 'error') failed = true;
      else total += hit.value;
    });
    if (failed) return null;
    if (pending) return undefined;
    return total;
  }, [flush, version, range]);

  return reachOf;
}

/**
 * Metrik nesneleri listesinin erişim toplamı. Meta nesneleri (`_mr`) ve karışık
 * toplamların Meta parçası (`_mrMix.spec`) BİRLEŞTİRİLİP tek değer olarak sorulur;
 * diğer platformların erişimi eskisi gibi toplanır. Meta değeri yükleniyorsa
 * undefined (tabloda "-"), hiç erişim yoksa undefined.
 */
export function sumReach(items, reachOf) {
  const metaSpecs = [];
  let sum = 0;
  let found = false;
  (items || []).forEach(it => {
    if (!it) return;
    if (it._mr) { metaSpecs.push(it._mr); return; }
    if (it._mrMix) {
      metaSpecs.push(it._mrMix.spec);
      if (it._mrMix.other) { sum += it._mrMix.other; found = true; }
      return;
    }
    if (it.erisim != null) { sum += Number(it.erisim) || 0; found = true; }
  });
  if (metaSpecs.length) {
    const v = reachOf(mrUnion(metaSpecs));
    if (v === undefined) return undefined;
    if (v != null) { sum += v; found = true; }
  }
  return found ? sum : undefined;
}

/**
 * Satır listesini sütun sütun toplar (percent/roas hariç); erişim sütunu
 * sumReach ile. Sonuç Meta içeriyorsa `_mrMix` taşır → bu toplam bir üst
 * toplamda tekrar kullanılırsa Meta kümesi kaybolmaz.
 */
export function sumMetrics(items, columns, reachOf) {
  const list = (items || []).filter(Boolean);
  const t = {};
  (columns || []).forEach(col => {
    if (col.format === 'percent' || col.format === 'roas') return;
    if (col.key === 'erisim') {
      const v = sumReach(list, reachOf);
      if (v !== undefined) t.erisim = v;
      return;
    }
    let sum = 0;
    let found = false;
    list.forEach(it => { const v = it[col.key]; if (v != null) { sum += v; found = true; } });
    if (found) t[col.key] = sum;
  });
  const specs = [];
  let other = 0;
  list.forEach(it => {
    if (it._mr) specs.push(it._mr);
    else if (it._mrMix) { specs.push(it._mrMix.spec); other += it._mrMix.other || 0; }
    else if (it.erisim != null) other += Number(it.erisim) || 0;
  });
  if (specs.length) t._mrMix = { spec: mrUnion(specs), other };
  return t;
}

/**
 * Nesne ağacında `_mr` taşıyan her nesnenin `erisim` alanını tekil değerle
 * değiştirir (kopya döner, girdiyi bozmaz). Yüklenirken/hata halinde erisim
 * undefined olur → tabloda "-" görünür; şişik DB değeri asla gösterilmez.
 */
export function withDedupReach(node, reachOf) {
  if (Array.isArray(node)) return node.map(n => withDedupReach(n, reachOf));
  if (!node || typeof node !== 'object' || node instanceof Set || node instanceof Date) return node;
  const out = {};
  Object.keys(node).forEach(k => { out[k] = k === '_mr' ? node[k] : withDedupReach(node[k], reachOf); });
  if (node._mr) {
    const v = reachOf(node._mr);
    out.erisim = v == null ? undefined : v;
  }
  return out;
}

/** Alt bileşenler reachOf'u prop zinciri olmadan buradan alır. */
export const MetaReachContext = createContext(() => undefined);
export const useReachOf = () => useContext(MetaReachContext);
